import { Sparkles, Wrench, Rocket, Smartphone, Globe, SlidersHorizontal } from "@/components/icons";
import type { ReactNode } from "react";
import type { Entry, Product } from "./types";

export const ALL_PRODUCTS: Product[] = ["mobile", "residential", "builder"];

export const PRODUCT_CONFIG: Record<
  Product,
  { label: string; short: string; icon: ReactNode; color: string; bg: string; border: string }
> = {
  mobile: {
    label: "Mobile Proxies",
    short: "Mobile",
    icon: <Smartphone size={11} />,
    color: "#3b82f6",
    bg: "#3b82f618",
    border: "#3b82f630",
  },
  residential: {
    label: "Residential Proxies",
    short: "Residential",
    icon: <Globe size={11} />,
    color: "#06b6d4",
    bg: "#06b6d418",
    border: "#06b6d430",
  },
  builder: {
    label: "Proxy Builder",
    short: "Builder",
    icon: <SlidersHorizontal size={11} />,
    color: "#a855f7",
    bg: "#a855f718",
    border: "#a855f730",
  },
};

export const CHANGELOG_ENTRIES: Entry[] = [
  {
    version: "1.3.0",
    date: "February 05, 2026",
    dateShort: "Feb 2026",
    products: ["builder"],
    changes: [
      {
        type: "feature",
        title: "Proxy Pooling",
        items: [
          "Group proxies into a shared pool with round-robin traffic distribution and optional sticky sessions.",
          "Pools update dynamically and support both HTTP and SOCKS proxies.",
        ],
      },
    ],
  },
  {
    version: "1.2.9",
    date: "January 19, 2026",
    dateShort: "Jan 2026",
    products: ["builder"],
    changes: [
      {
        type: "feature",
        title: "Dynamic Port Mapping",
        items: ["Dynamic port mapping is now live for both HTTP and SOCKS proxies."],
      },
    ],
  },
  {
    version: "Authentication Update",
    date: "January 15, 2026",
    dateShort: "Jan 2026",
    isSpecial: true,
    products: ["mobile"],
    changes: [
      {
        type: "improvement",
        title: "Password-less Login",
        items: [
          "Password-less login is now live across the Proxidize platform.",
          "All logins are moving to OTP (one-time passcode) authentication instead of passwords.",
          "This change is effective immediately for all new and existing Proxidize users.",
        ],
      },
    ],
  },
  {
    version: "1.2.8",
    date: "January 13, 2026",
    dateShort: "Jan 2026",
    products: ["residential"],
    changes: [
      {
        type: "fix",
        title: "Analytics Sync",
        items: ["Fixed analytics syncing with usage data."],
      },
    ],
  },
  {
    version: "1.2.7",
    date: "January 08, 2026",
    dateShort: "Jan 2026",
    products: ["mobile"],
    changes: [
      {
        type: "improvement",
        title: "KYC Integration",
        items: ["KYC integration added to the onboarding flow."],
      },
    ],
  },
  {
    version: "1.2.6",
    date: "December 01, 2025",
    dateShort: "Dec 2025",
    products: ["mobile"],
    changes: [
      {
        type: "improvement",
        title: "Speed & Usage Tracking",
        items: [
          "Added average speed to locations API.",
          "Added speed tracking per proxy session.",
          "Added total bytes counter for per-GB usage.",
        ],
      },
    ],
  },
  {
    version: "1.2.5",
    date: "October 27, 2025",
    dateShort: "Oct 2025",
    products: ["residential"],
    changes: [
      {
        type: "improvement",
        title: "Proxy Tags",
        items: ["Added tags to proxies for easier filtering and organization."],
      },
    ],
  },
  {
    version: "1.2.2",
    date: "August 19, 2025",
    dateShort: "Aug 2025",
    products: ["builder"],
    changes: [
      {
        type: "fix",
        title: "Packet & Error Handling",
        items: [
          "Added byte counting for UDP packets.",
          "Added error response handling for clients.",
        ],
      },
    ],
  },
  {
    version: "1.2.1",
    date: "August 01, 2025",
    dateShort: "Aug 2025",
    products: ["builder"],
    changes: [
      {
        type: "improvement",
        title: "API & IP Fetching",
        items: [
          "Added endpoint to return info for a single per-proxy proxy.",
          "Server-side IP address fetching.",
        ],
      },
    ],
  },
  {
    version: "1.2.0",
    date: "July 28, 2025",
    dateShort: "Jul 2025",
    products: ["mobile"],
    changes: [
      {
        type: "improvement",
        title: "Performance & Routing",
        items: [
          "Added string values for per-proxy location APIs.",
          "Reduced production logging and increased session retention.",
          "Added UDP SOCKS5 support.",
          "Improved analytics, routing, proxy filtering, and concurrency handling.",
        ],
      },
      {
        type: "fix",
        title: "Stability Fixes",
        items: [
          "Fixed country handling in rotation endpoint.",
          "Fixed session stability.",
          "Fixed UDP packet loss.",
          "Multiple production stability and performance fixes.",
        ],
      },
    ],
  },
  {
    version: "1.1.0",
    date: "June 25, 2025",
    dateShort: "Jun 2025",
    products: ["residential"],
    changes: [
      {
        type: "improvement",
        title: "Deployment Locations",
        items: ["Added environment dropdown and new deployment locations."],
      },
    ],
  },
  {
    version: "1.0",
    date: "Initial Release",
    dateShort: "2025",
    isInitial: true,
    products: ["mobile"],
    changes: [
      {
        type: "feature",
        title: "Core Platform",
        items: [
          "Custom connection handling and byte counting.",
          "Real-time user enable/disable.",
          "Proxy speed checker service.",
          "Session management and analytics.",
          "Numerous stability, crash, byte-counting, analytics, and routing fixes.",
        ],
      },
    ],
  },
];

export const CHANGE_TYPE_CONFIG = {
  feature: {
    label: "New Feature",
    icon: <Sparkles size={12} />,
    color: "var(--yellow-accent)",
    bg: "#22d3ee18",
    border: "#22d3ee30",
  },
  improvement: {
    label: "Improvement",
    icon: <Rocket size={12} />,
    color: "var(--code-green)",
    bg: "#5b8cff18",
    border: "#5b8cff30",
  },
  fix: {
    label: "Fix",
    icon: <Wrench size={12} />,
    color: "var(--amber-accent)",
    bg: "#f59e0b18",
    border: "#f59e0b30",
  },
} as const;
